import { useState, useEffect, Suspense } from "react";
import {
  Route,
  Routes,
  Link,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import "./i18n";

import ExerciseMenu from "./components/Exercise/ExerciseMenu";
import ProgramMenu from "./components/Program/ProgramMenu";
import Home from "./components/Home/Home";

import CycleMenu2 from "./components/Cycle2/CycleMenu2.jsx";
import CycleDetails from "./components/Cycle2/CycleDetails.jsx";

import PhaseMenu from "./components/ProgramPhase/PhaseMenu";
import Login from "./components/Authentication/Login";
import PatientMenu from "./components/Patient/PatientMenu";

import DoctorMenu from "./components/ProfessionalUser/Doctor/DoctorMenu";
import DoctorPatients from "./components/ProfessionalUser/Doctor/DoctorPatients";

import KinesiologistMenu from "./components/ProfessionalUser/Kinesiologist/KinesiologistMenu";
import KinesiologistMessaging from "./components/ProfessionalUser/Kinesiologist/KinesiologistMessaging";
import KinesiologistPatients from "./components/ProfessionalUser/Kinesiologist/KinesiologistPatients";

import AdminMenu from "./components/ProfessionalUser/Admin/AdminMenu";

import EvaluationPACE from "./components/Evaluation/EvaluationPACE";
import EvaluationMATCH from "./components/Evaluation/EvaluationMATCH";
import EvaluationPATH from "./components/Evaluation/EvaluationPATH";
import EvaluationSearch from "./components/Evaluation/EvaluationSearch";
import EvaluationDisplay from "./components/Evaluation/EvaluationDisplay";
import AccessRequests from "./components/AccessRequests/AccessRequests";
import CalendarCreationUI from "./components/Calendrier/CalendarCreationUI";
import useToken from "./components/Authentication/useToken";
import Constants from "./components/Utils/Constants";
import LanguageSwitcher from "./components/LanguageSwitcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";

import DoctorEligibilityRequestForm from "./components/Eligibility/DoctorEligibilityRequestForm";
import EligibilityRequestList from "./components/Eligibility/EligibilityRequestList";
import DoctorEligibilityList from "./components/Eligibility/DoctorEligibilityList";

import BlocMenu2 from "./components/Bloc2/BlocMenu2";
import BlocDetails2 from "./components/Bloc2/BlocDetails2";

import SeanceMenu from "./components/seances/SeanceMenu";
import SeanceDetails from "./components/seances/SeanceDetails";

import ProgrammeMenu from "./components/programmes/ProgrammeMenu";
import ProgrammeList from "./components/programme2/ProgrammeList.jsx";
import ProgrammeDetails from "./components/programme2/ProgrammeDetails";
import { ProgrammeAssign } from "./components/programme2/ProgrammeAssign";
import ProgrammeModifier from "./components/programme2/ProgrammeModifier";

import FollowUpCallPACE from "./components/FollowUpCall/FollowUpCallPACE";

import { Layout, Menu, Button, Avatar, Dropdown } from "antd";
import {
  HomeOutlined,
  AppstoreOutlined,
  SettingOutlined,
  BlockOutlined,
  CalendarOutlined,
  ClusterOutlined,
  BellOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  MessageOutlined,
  FormOutlined,
  BarChartOutlined,
  BankOutlined,
  SolutionOutlined,
} from "@ant-design/icons";

import "antd/dist/reset.css";
import "./App.css";

import SetNewPassword from "./components/Authentication/SetNewPassword";
import ResetPassword from "./components/Authentication/ResetPassword";
import ForgotPassword from "./components/Authentication/ForgotPassword";
import DemandeSignup from "./components/Authentication/DemandeSignup";

import PatientStatsHub from "./components/Patient/PatientStatsHub";
import ClinicsMenu from "./components/Clinic/ClinicsMenu";
import ClinicManagerMenu from "./components/ProfessionalUser/ClinicManager/ClinicManagerMenu";

const { Header, Sider, Content } = Layout;

function finalRoleFromRoles(finalRoles, roleFromState, roleFromStorage) {
  if (
    finalRoles.includes("kinesiologist") &&
    finalRoles.includes("clinic_manager")
  ) {
    return "kinesiologist";
  }

  return roleFromState || roleFromStorage || finalRoles[0] || "";
}

function parseRolesFromStoredToken() {
  const token = localStorage.getItem("token");

  if (!token) {
    return [];
  }

  try {
    const payloadPart = token.split(".")[1];

    if (!payloadPart) {
      return [];
    }

    const normalizedBase64 = payloadPart
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payloadPart.length / 4) * 4, "=");

    const payload = JSON.parse(atob(normalizedBase64));

    if (Array.isArray(payload?.roles)) {
      return payload.roles.map((role) => String(role).toLowerCase());
    }

    if (payload?.role) {
      return [String(payload.role).toLowerCase()];
    }
  } catch (_error) {
    // Ignore invalid or stale tokens and fall back to local storage state.
  }

  return [];
}

function App() {
  const { t, i18n } = useTranslation("App");
  const location = useLocation();
  const navigate = useNavigate();
  const { token, setToken } = useToken();

  const [selectedKey, setSelectedKey] = useState(location.pathname);
  const [role, setRole] = useState("");
  const [firstname, setFirstname] = useState(
    () => localStorage.getItem("firstname") || ""
  );

  const [menuItems, setMenuItems] = useState([]);
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);

  useEffect(() => {
    const newMenuItems = [
      {
        key: "/",
        icon: <HomeOutlined />,
        label: <Link to="/">{t("label_dashboard")}</Link>,
      },

      {
        key: "professional-users",
        icon: <UsergroupAddOutlined />,
        label: t("label_professional_users"),
        children: [
          {
            key: "/admins",
            icon: <UsergroupAddOutlined />,
            label: <Link to="/admins">{t("label_admins")}</Link>,
          },
          {
            key: "/doctors",
            icon: <MedicineBoxOutlined />,
            label: <Link to="/doctors">{t("label_physicians")}</Link>,
          },
          {
            key: "/kinesiologists",
            icon: <HeartOutlined />,
            label: (
              <Link to="/kinesiologists">{t("label_kinesiologists")}</Link>
            ),
          },
          {
            key: "/clinic-managers",
            icon: <SolutionOutlined />,
            label: (
              <Link to="/clinic-managers">
                {t("label_clinic_managers")}
              </Link>
            ),
          },
{
  key: "/AccessRequests",
  label: <Link to="/AccessRequests">{t("label_access_requests")}</Link>,
},
        ],
      },

      {
        key: "/patients",
        icon: <UserOutlined />,
        label: <Link to="/patients">{t("label_patients")}</Link>,
      },

      {
        key: "/evaluations",
        icon: <FormOutlined />,
        label: <Link to="/evaluations">{t("label_evaluation")}</Link>,
      },

      {
        key: "/calendar",
        icon: <CalendarOutlined />,
        label: <Link to="/calendar">{t("label_calendar")}</Link>,
      },

      {
        key: "/exercises",
        icon: <AppstoreOutlined />,
        label: <Link to="/exercises">{t("label_exercises")}</Link>,
      },

      {
        key: "/blocs2",
        icon: <BlockOutlined />,
        label: <Link to="/blocs2">{t("label_blocs")}</Link>,
      },

      {
        key: "/seances",
        icon: <FormOutlined />,
        label: <Link to="/seances">{t("label_sessions")}</Link>,
      },

      {
        key: "/cycles2",
        icon: <ClusterOutlined />,
        label: <Link to="/cycles2">{t("label_cycles")}</Link>,
      },

      {
        key: "/programme2",
        icon: <SettingOutlined />,
        label: <Link to="/programme2">{t("label_programs")}</Link>,
      },

      {
        key: "/doctor-eligibility-request",
        icon: <FormOutlined />,
        label: (
          <Link to="/doctor-eligibility-request">
            {t("label_eligibility_request")}
          </Link>
        ),
      },

      {
        key: "eligibility-requests-received",
        icon: <FormOutlined />,
        label: t("label_eligibility_requests_received"),
        children: [
          {
            key: "/doctor-eligibility-list-manager",
            icon: <SolutionOutlined />,
            label: (
              <Link to="/doctor-eligibility-list-manager">
                {t("label_clinic_managers")}
              </Link>
            ),
          },
          {
            key: "/doctor-eligibility-list-kinesiologist",
            icon: <HeartOutlined />,
            label: (
              <Link to="/doctor-eligibility-list-kinesiologist">
                {t("label_kinesiologists")}
              </Link>
            ),
          },
        ],
      },

      {
        key: "/doctorKin-eligibility-list",
        icon: <FormOutlined />,
        label: (
          <Link to="/doctorKin-eligibility-list">
            {t("label_sent_requests")}
          </Link>
        ),
      },

      {
        key: "/patient-stats",
        icon: <BarChartOutlined />,
        label: <Link to="/patient-stats">{t("label_statistics")}</Link>,
      },

      {
        key: "/kinesiologist-messaging",
        icon: <MessageOutlined />,
        label: (
          <Link to="/kinesiologist-messaging">
            {t("label_messaging")}
          </Link>
        ),
      },

      {
        key: "/clinics",
        icon: <BankOutlined />,
        label: <Link to="/clinics">{t("label_clinics")}</Link>,
      },
    ];

    setMenuItems(newMenuItems);
    setFilteredMenuItems(newMenuItems);
  }, [i18n.language, t]);

  useEffect(() => {
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  useEffect(() => {
    setSelectedKey(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const roleFromState = String(location.state?.role || "").toLowerCase();
    const roleFromStorage = String(
      localStorage.getItem("userRole") || ""
    ).toLowerCase();

    let rolesFromStorage = [];

    try {
      const parsed = JSON.parse(localStorage.getItem("userRoles") || "[]");
      rolesFromStorage = Array.isArray(parsed)
        ? parsed.map((r) => String(r).toLowerCase())
        : [];
    } catch {
      rolesFromStorage = [];
    }

    const rolesFromState = Array.isArray(location.state?.roles)
      ? location.state.roles.map((r) => String(r).toLowerCase())
      : [];

    const rolesFromToken = parseRolesFromStoredToken();

    const finalRoles = Array.from(
      new Set(
        [
          ...rolesFromStorage,
          ...rolesFromState,
          ...rolesFromToken,
          roleFromState,
          roleFromStorage,
        ].filter(Boolean)
      )
    );

    const finalRole = finalRoleFromRoles(
      finalRoles,
      roleFromState,
      roleFromStorage
    );

    const firstnameFromState = location.state?.firstname;
    const firstnameFromStorage = localStorage.getItem("firstname");
    const finalFirstname = firstnameFromState || firstnameFromStorage;

    if (finalRole) {
      setRole(finalRole);
    }

    if (finalFirstname) {
      setFirstname(finalFirstname);
      localStorage.setItem("firstname", finalFirstname);
    }

    const doctorAllowed = [
      "/",
      "/patients",
      "/programs",
      "/patient-stats",
      "/doctor-eligibility-request",
      "/doctorKin-eligibility-list",
    ];

    const kinesiologistAllowed = [
      "/",
      "/patients",
      "/programs",
      "/patient-stats",
      "/evaluations",
      "/calendar",
      "/exercises",
      "/blocs2",
      "/seances",
      "/cycles2",
      "/programme2",
      "/doctor-eligibility-list-kinesiologist",
      "/kinesiologist-messaging",
    ];

    const clinicManagerAllowed = [
      "/",
      "/doctor-eligibility-request",
      "/doctor-eligibility-list-manager",
      "/clinics",
      "/blocs2",
      "/cycles2",
    ];

    const hasAdminRole =
      finalRoles.includes("admin") || finalRoles.includes("superadmin");

    const hasRestrictedRole =
      finalRoles.includes("doctor") ||
      finalRoles.includes("kinesiologist") ||
      finalRoles.includes("clinic_manager");

    const allowedSet = new Set();

    if (!hasAdminRole && hasRestrictedRole) {
      if (finalRoles.includes("doctor")) {
        doctorAllowed.forEach((key) => allowedSet.add(key));
      }

      if (finalRoles.includes("kinesiologist")) {
        kinesiologistAllowed.forEach((key) => allowedSet.add(key));
      }

      if (finalRoles.includes("clinic_manager")) {
        clinicManagerAllowed.forEach((key) => allowedSet.add(key));
      }
    }

    const isManagerKinesiologist =
      !hasAdminRole &&
      finalRoles.includes("clinic_manager") &&
      finalRoles.includes("kinesiologist");

    const isManagerOnly =
      !hasAdminRole &&
      finalRoles.includes("clinic_manager") &&
      !finalRoles.includes("kinesiologist");

    const isKinesiologistOnly =
      !hasAdminRole &&
      finalRoles.includes("kinesiologist") &&
      !finalRoles.includes("clinic_manager");

    const filterMenuItems = (items) =>
      items
        .map((item) => {
          if (item.children) {
            const filteredChildren = filterMenuItems(item.children);

            if (filteredChildren.length > 0) {
              return { ...item, children: filteredChildren };
            }

            return null;
          }

          if (!hasAdminRole && hasRestrictedRole) {
            return allowedSet.has(item.key) ? item : null;
          }

          return item;
        })
        .filter((item) => item !== null);

    const filteredMenu = filterMenuItems(menuItems);

    if (isManagerOnly) {
      const managerMenuWithoutDropdown = filteredMenu
        .filter((item) => item.key !== "/doctor-eligibility-request")
        .map((item) => {
          if (item.key !== "eligibility-requests-received") {
            return item;
          }

          return {
            key: "/doctor-eligibility-list-manager",
            icon: item.icon,
            label: (
              <Link to="/doctor-eligibility-list-manager">
                {t("label_eligibility_requests_received")}
              </Link>
            ),
          };
        });

      setFilteredMenuItems(managerMenuWithoutDropdown);
      return;
    }

    if (isKinesiologistOnly) {
      const kinesiologistMenuWithoutDropdown = filteredMenu.map((item) => {
        if (item.key !== "eligibility-requests-received") {
          return item;
        }

        return {
          key: "/doctor-eligibility-list-kinesiologist",
          icon: item.icon,
          label: (
            <Link to="/doctor-eligibility-list-kinesiologist">
              {t("label_eligibility_requests_received")}
            </Link>
          ),
        };
      });

      setFilteredMenuItems(kinesiologistMenuWithoutDropdown);
      return;
    }

    if (isManagerKinesiologist) {
      const filteredMenuWithEligibilityDivider = filteredMenu.map((item) => {
        if (
          item.key !== "eligibility-requests-received" ||
          !Array.isArray(item.children)
        ) {
          return item;
        }

        const managerRequestItem = item.children.find(
          (child) => child.key === "/doctor-eligibility-list-manager"
        );

        const kinesiologistRequestItem = item.children.find(
          (child) => child.key === "/doctor-eligibility-list-kinesiologist"
        );

        if (!managerRequestItem || !kinesiologistRequestItem) {
          return item;
        }

        return {
          ...item,
          children: [managerRequestItem, kinesiologistRequestItem],
        };
      });

      const managerSectionKeys = new Set([
        "/clinics",
      ]);

      const kinesiologistSectionKeys = new Set([
        "/patients",
        "/evaluations",
        "/calendar",
        "/exercises",
        "/blocs2",
        "/seances",
        "/cycles2",
        "/programme2",
        "eligibility-requests-received",
        "/patient-stats",
        "/kinesiologist-messaging",
      ]);

      const sharedItems = [];
      const managerItems = [];
      const kinesiologistItems = [];
      const remainingItems = [];

      filteredMenuWithEligibilityDivider.forEach((item) => {
        if (item.key === "/") {
          sharedItems.push(item);
          return;
        }

        if (item.key === "/doctor-eligibility-request") {
          return;
        }

        if (managerSectionKeys.has(item.key)) {
          managerItems.push(item);
          return;
        }

        if (kinesiologistSectionKeys.has(item.key)) {
          kinesiologistItems.push(item);
          return;
        }

        remainingItems.push(item);
      });

      const managerKinesiologistMenu = [
        ...sharedItems,
        ...managerItems,
        ...(managerItems.length > 0 && kinesiologistItems.length > 0
          ? [
              {
                type: "divider",
                key: "__manager_kinesiologist_menu_divider__",
                className: "manager-kinesiologist-menu-divider",
              },
            ]
          : []),
        ...kinesiologistItems,
        ...remainingItems,
      ];

      setFilteredMenuItems(managerKinesiologistMenu);
      return;
    }

    const adminMenu = hasAdminRole
      ? filteredMenu.filter((item) => item.key !== "/doctorKin-eligibility-list")
      : filteredMenu;

    setFilteredMenuItems(adminMenu);
  }, [location.state, menuItems, t]);

  const handleLogout = async () => {
    try {
      await axios.post(`${Constants.SERVER_URL}/logout`);

      setToken(null);
      localStorage.removeItem("userRole");
      localStorage.removeItem("userRoles");
      localStorage.removeItem("firstname");
      localStorage.removeItem("token");

      navigate("/login");
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  };

  const userMenuItems = [
    {
      key: "1",
      label: <Link to="/profile">{t("label_profile")}</Link>,
    },
    {
      key: "2",
      label: <Link to="/settings">{t("label_settings")}</Link>,
    },
    {
      key: "3",
      label: t("label_logout"),
      onClick: handleLogout,
    },
  ];

  if (!token) {
    return (
      <Content className="content">
        <Routes>
          <Route path="login" element={<Login setToken={setToken} />} />
          <Route path="/set-new-password" element={<SetNewPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/Demande-signup" element={<DemandeSignup />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Content>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={280}
        className={`sider ${role}-sidebar ${
          i18n.language === "ar" ? "sider-ar" : ""
        }`}
      >
        <div className="logo">RxAPA</div>

        <Menu
          theme="dark"
          selectedKeys={[selectedKey]}
          mode="inline"
          items={filteredMenuItems}
        />
      </Sider>

      <Layout
        className={`site-layout ${
          i18n.language === "ar" ? "site-layout-ar" : ""
        }`}
      >
        <Header
          className={`header site-layout-background ${
            i18n.language === "ar" ? "header-ar" : ""
          }`}
        >
          <div></div>

          <div className="header-content">
            <LanguageSwitcher
              iconStyle={{ color: "white" }}
              iconClassName="header-language-icon"
            />

            <Button icon={<SettingOutlined />} className="header-button" />
            <Button icon={<BellOutlined />} className="header-button" />

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div className="header-avatar">
                <Avatar icon={<UserOutlined />} />
                <span>{firstname || role}</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="content">
          <Routes>
            <Route path="/" element={<Home />} />

            <Route path="exercises" element={<ExerciseMenu />} />

            <Route path="calendar" element={<CalendarCreationUI />} />

            <Route path="blocs2" element={<BlocMenu2 />} />
            <Route path="/blocs2/:blocKey" element={<BlocDetails2 />} />

            <Route path="cycles2" element={<CycleMenu2 />} />
            <Route path="/cycles/:key" element={<CycleDetails />} />

            <Route path="phases" element={<PhaseMenu />} />
            <Route path="programs" element={<ProgramMenu />} />

            <Route
              path="doctor-eligibility-request"
              element={<DoctorEligibilityRequestForm />}
            />

            <Route
              path="doctor-eligibility-list"
              element={<EligibilityRequestList />}
            />

            <Route
              path="doctor-eligibility-list-manager"
              element={<EligibilityRequestList viewMode="manager" />}
            />

            <Route
              path="doctor-eligibility-list-kinesiologist"
              element={<EligibilityRequestList viewMode="kinesiologist" />}
            />

            <Route
              path="doctorKin-eligibility-list"
              element={<DoctorEligibilityList />}
            />

            <Route path="seances" element={<SeanceMenu />} />
            <Route path="seances/:sessionKey" element={<SeanceDetails />} />

            <Route path="/programme" element={<ProgrammeMenu />} />
            <Route
              path="/programme/:programmeId"
              element={<ProgrammeDetails />}
            />

            <Route
              path="/programmes/:programmeId"
              element={<ProgrammeDetails />}
            />

            <Route
              path="/programme2/assign/:patientId"
              element={<ProgrammeAssign />}
            />

            <Route
              path="/programme2/:programmeId/edit"
              element={<ProgrammeModifier />}
            />

            <Route
              path="/programme2/:programmeId"
              element={<ProgrammeDetails />}
            />

            <Route path="/programme2" element={<ProgrammeList />} />

            <Route path="/patient-stats" element={<PatientStatsHub />} />

            <Route path="clinics" element={<ClinicsMenu />} />

            <Route path="patients" element={<PatientMenu role={role} />} />

            <Route path="doctors" element={<DoctorMenu />} />

            <Route
              path="doctor-patients/:id"
              element={<DoctorPatients />}
            />

            <Route
              path="kinesiologists"
              element={<KinesiologistMenu />}
            />

            <Route
              path="clinic-managers"
              element={<ClinicManagerMenu />}
            />
<Route path="AccessRequests" element={<AccessRequests />} />

            <Route
              path="kinesiologist-patients/:id"
              element={<KinesiologistPatients />}
            />

            <Route
              path="kinesiologist-messaging"
              element={<KinesiologistMessaging />}
            />

            <Route path="admins" element={<AdminMenu />} />

            <Route
              path="evaluations"
              element={
                <Suspense fallback={<div>{t("title_loading_evaluations")}</div>}>
                  <EvaluationSearch />
                </Suspense>
              }
            />

            <Route
              path="evaluation-pace/:patientId"
              element={
                <Suspense fallback={<div>{t("title_loading_evaluations")}</div>}>
                  <EvaluationPACE />
                </Suspense>
              }
            />

            <Route
              path="evaluation-match/:patientId"
              element={
                <Suspense fallback={<div>{t("title_loading_evaluations")}</div>}>
                  <EvaluationMATCH />
                </Suspense>
              }
            />

            <Route
              path="evaluation-path/:patientId"
              element={
                <Suspense fallback={<div>{t("title_loading_evaluations")}</div>}>
                  <EvaluationPATH />
                </Suspense>
              }
            />

            <Route
              path="evaluations/patient/:patientId"
              element={
                <Suspense fallback={<div>{t("title_loading_evaluations")}</div>}>
                  <EvaluationDisplay />
                </Suspense>
              }
            />

            <Route
              path="follow-up-pace/:patientId"
              element={
                <Suspense fallback={<div>{t("title_loading_followup")}</div>}>
                  <FollowUpCallPACE />
                </Suspense>
              }
            />

            <Route
              path="*"
              element={
                <main style={{ padding: "1rem" }}>
                  <p>{t("There's nothing here!")}</p>
                </main>
              }
            />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

export default App;
